import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LuCoins, LuLeaf, LuWeight } from "react-icons/lu";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { TbBrandCarbon } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import CircularChart from "../../circularity-components/CircularChart";
import MaterialDetailCard from "../../circularity-components/MaterialDetailCard";
import NomarlizedGwpVsPercentageMciChart from "../../circularity-components/NomarlizedGwpVsPercentageMciChart";
import ProjectMCICalculationTable from "../../circularity-components/ProjectMCICalculationTable";
import SaveButton from "../../components/button/SaveButton";
import { MainContainer } from "../../components/container/MainContainer";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import ReportButton from "../../components/button/ReportButton";
import DesignAssessmentPanel from "../Products/Circularity/DesignAssessmentPanel";
import ScenarioPanel from "../Products/Circularity/ScenarioPanel";
import BenchmarkPanel from "../Products/Circularity/BenchmarkPanel";
import CircularityReportPanel from "../Products/Circularity/CircularityReportPanel";
import ScopeBoundaryPanel from "../Products/Circularity/ScopeBoundaryPanel";
import CircularityAdminTabControls, { useCircularityAdminTabs } from "../../circularity-components/CircularityAdminTabControls";
import { useInvalidateBuildingCache } from "../../utilities/useBuildingQueries";
import { Circularity } from "../../db_access/circularity";
import { EnvFactor } from "../../db_access/env_factor";
import { Building } from "../../db_access/building";
import { Process } from "../../db_access/process";
import BuildingProductDropdown, { ALL_PRODUCTS_VALUE } from "./components/BuildingProductDropdown";
import { calculate_combined_data, recalculate_row_mci } from "../../functions/CircularityFunctions";
import routing from "../../routing";
import "../../style/components-style/Table.css";
import "../../style/pages-style/Circularity.css";
import MetricCard from "../../app_components/MetricCard";
import InputMaterialChart from "../Products/Circularity/InputMaterialChart";
import BuildingMaterialCompositionTable from "./components/BuildingMaterialCompositionTable";
import BuildingSCICalculationTable from "./components/BuildingSCICalculationTable";
import BuildingBCICalculationTable from "./components/BuildingBCICalculationTable";
import { computeBciMetrics, truncateTo2Decimals } from "./components/shearingLayers";
import OutputMaterialChart from "../Products/Circularity/OutputMaterialChart";
import UtilityTable from "../Products/Circularity/UtilityTable";
import config from "../../config";
import InfoBanner from "../../components/banner/InfoBanner";

const BuildingCircularity = () => {
  const { t } = useTranslation();
  const { selectedBuildings, refresh, setRefresh, setIsSaveVisible, setIsLoading, isSaveClicked, setIsSaveClicked, isDark, enqueueAlert, setLeftSidebarCollapse } = useContextProvider();
  const navigate = useNavigate();
  const scope_boundary = { label: "Boundary Settings", value: "Boundary Settings" };
  const overview = { label: "Overview", value: "Overview" };
  const utility = { label: "Utility", value: "Utility" };
  const mci_calculation = { label: "MCI Calculation", value: "MCI Calculation" };
  const sci_calculation = { label: "SCI Calculation", value: "SCI Calculation" };
  const bci_calculation = { label: "BCI Calculation", value: "BCI Calculation" };
  const analytics = { label: "Analytics", value: "Analytics" };

  // Design Assessment is always visible in the tab switcher for Building Circularity
  // (unlike Product/Project, where it's gated behind the Scope & Boundary "Design" toggle).
  // scopeDesign still defaults on so the ScopeBoundaryPanel toggle reads as enabled.
  const [scopeDesign, setScopeDesign] = useState(true);
  const [scopeScenario, setScopeScenario] = useState(false);

  const design_assessment = { label: "Design Assessment", value: "Design Assessment" };
  const scenario_what_if = { label: "Scenario/What-if", value: "Scenario/What-if" };
  const benchmarking = { label: "AI Recommendation", value: "AI Recommendation" };
  const pcds_report = { label: "Circularity Report", value: "Circularity Report", tabIcon: SummarizeIcon };

  const {
    isAdmin,
    adminTabsPrefs,
    updatePref,
    setAllPrefs,
    showUtilityTab,
    showMciCalcTab,
    showAiRecommendTab,
    activeAdminTabsCount,
  } = useCircularityAdminTabs();

  const isoTabs = [
    ...(scopeDesign ? [design_assessment] : []),
    ...(scopeScenario ? [scenario_what_if] : []),
    ...(showAiRecommendTab ? [benchmarking] : []),
  ];

  const [materials, setMaterials] = useState([]);
  const [circularities, setCircularities] = useState([]);
  const [selectedEnvFactorID, setSelectedEnvFactorID] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState();
  const [activeTab, setActiveTab] = useState(overview.value);

  // ─── Shared Products dropdown (Material Composition / Utility / MCI Calculation) ──
  // Lifted here so all three tabs read/drive the same selection and share a single
  // fetch instead of each tab independently fetching processes + WBLCA data.
  const [processes, setProcesses] = useState([]);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(ALL_PRODUCTS_VALUE);
  // null => no product selected; the three tabs fall back to the whole-building combined_data
  const [productCombinedData, setProductCombinedData] = useState(null);
  // Which selectedSubcategoryId productCombinedData was actually fetched for — lets MCI
  // Calculation/Design Assessment tell "still fetching the newly picked subcategory" apart
  // from "already showing it", so they never flash the previous selection's data.
  const [loadedSubcategoryId, setLoadedSubcategoryId] = useState(null);
  // SCI/BCI Calculation always show every product, independent of the dropdown above —
  // fetched separately so switching the dropdown never filters those two tabs.
  const [allProductsCombinedData, setAllProductsCombinedData] = useState(null);

  // Auto-switch to overview when a hidden tab is active
  // (Design Assessment is always visible now, so it's excluded from this check.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === scenario_what_if.value && !scopeScenario) setActiveTab(overview.value);
    if (activeTab === utility.value && !showUtilityTab) setActiveTab(overview.value);
    if (activeTab === mci_calculation.value && !showMciCalcTab) setActiveTab(overview.value);
    if (activeTab === benchmarking.value && !showAiRecommendTab) setActiveTab(overview.value);
  }, [scopeScenario, showUtilityTab, showMciCalcTab, showAiRecommendTab, activeTab]);

  useEffect(() => {
    if (activeTab === design_assessment.value) {
      setLeftSidebarCollapse(true);
    }
  }, [activeTab, setLeftSidebarCollapse]);

  const [product_mci, setProduct_mci] = useState(0);
  const [percentage_product_mci, setPercentage_product_mci] = useState(0);
  const [mci_sum, setMci_sum] = useState(0);
  const [mass_sum, setMass_sum] = useState(0);
  const [cost_sum, setCost_sum] = useState(0);
  const [gwp_total_sum, setGwp_total_sum] = useState(0);
  const [combined_circularities, setCombined_circularities] = useState([]);
  const [service_life, setService_life] = useState(0);
  const tabs = [overview, utility, mci_calculation, design_assessment, sci_calculation, bci_calculation, analytics];

  // AI State Variables
  const [aiLoading, setAiLoading] = useState(false);
  const aiTriggeredForId = useRef(null);
  const [showInfoBar, setShowInfoBar] = useState(false);

  // Ref for the silent MCI auto-sync debounce timer
  const mciSyncTimeoutRef = useRef(null);
  const { invalidateDashboard } = useInvalidateBuildingCache();
  const [isNewProduct, setIsNewProduct] = useState(false);

  // Indicator scope toggles (Scope & Boundary) — shared with the Utility tab's sub-tabs.
  // Persisted to localStorage per building so they survive a page refresh or navigating
  // away and back (see the load/save effects below).
  const [scopeEnergy, setScopeEnergy] = useState(false);
  const [scopeWater, setScopeWater] = useState(false);
  const [scopeEconomic, setScopeEconomic] = useState(false);

  // ScopeBoundaryPanel lifted configurations
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-12-31");
  const [cutoff, setCutoff] = useState("Fiscal year-end");
  const [flow, setFlow] = useState("gate");
  const [materiality, setMateriality] = useState(95);

  const boundaryState = useMemo(() => ({
    start, end, cutoff, flow, materiality
  }), [start, end, cutoff, flow, materiality]);

  // ─── Persist Scope & Boundary toggles + selected product dropdown, per building ────
  // Survives a page refresh or navigating away and back.
  const scopeTogglesKey = selectedBuilding?.id ? `arivu_building_circularity_prefs_${selectedBuilding.id}` : null;
  const scopeTogglesLoaded = useRef(false);

  useEffect(() => {
    scopeTogglesLoaded.current = false;
    if (!scopeTogglesKey) return;
    try {
      const saved = localStorage.getItem(scopeTogglesKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setScopeEnergy(!!parsed.scopeEnergy);
        setScopeWater(!!parsed.scopeWater);
        setScopeEconomic(!!parsed.scopeEconomic);
        setScopeDesign(parsed.scopeDesign !== undefined ? !!parsed.scopeDesign : true);
        setScopeScenario(!!parsed.scopeScenario);
        setSelectedSubcategoryId(parsed.selectedSubcategoryId || ALL_PRODUCTS_VALUE);
        if (parsed.start) setStart(parsed.start);
        if (parsed.end) setEnd(parsed.end);
        if (parsed.cutoff) setCutoff(parsed.cutoff);
        if (parsed.flow) setFlow(parsed.flow);
        if (typeof parsed.materiality === "number") setMateriality(parsed.materiality);
      } else {
        setScopeEnergy(false);
        setScopeWater(false);
        setScopeEconomic(false);
        setScopeDesign(true);
        setScopeScenario(false);
        setSelectedSubcategoryId(ALL_PRODUCTS_VALUE);
      }
    } catch (e) {
      console.error("Failed to load Building Circularity preferences", e);
    } finally {
      scopeTogglesLoaded.current = true;
    }
  }, [scopeTogglesKey]);

  useEffect(() => {
    // Skip the write that would otherwise fire immediately after the load effect above
    // sets these same values back onto state (harmless, but avoids a redundant write).
    if (!scopeTogglesKey || !scopeTogglesLoaded.current) return;
    localStorage.setItem(
      scopeTogglesKey,
      JSON.stringify({
        scopeEnergy,
        scopeWater,
        scopeEconomic,
        scopeDesign,
        scopeScenario,
        selectedSubcategoryId,
        start,
        end,
        cutoff,
        flow,
        materiality,
      })
    );
  }, [
    scopeTogglesKey,
    scopeEnergy,
    scopeWater,
    scopeEconomic,
    scopeDesign,
    scopeScenario,
    selectedSubcategoryId,
    start,
    end,
    cutoff,
    flow,
    materiality,
  ]);

  const combined_data = useMemo(() => {
    return { mass_sum, cost_sum, gwp_total_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities, service_life };
  }, [mass_sum, cost_sum, gwp_total_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities, service_life]);

  // ─── Shared Products dropdown fetch ────────────────────────────────────────
  useEffect(() => {
    if (!selectedBuilding?.id) {
      setProcesses([]);
      return;
    }
    Process.get_by_building_ids([selectedBuilding.id])
      .then((res) => res.json())
      .then((data) => setProcesses(data || []))
      .catch((err) => console.error("Error fetching building processes:", err));
  }, [selectedBuilding?.id, refresh]);

  // "All Products" must NOT fall back to the restricted combined_data above (that's
  // built from EnvFactor.get_materials_by_project_id, which filters to Product/Project-
  // style types and A1-A5 LCA stages — real WBLCA building materials are often outside
  // that restriction, which is exactly why the per-product fetch below bypasses it). So
  // "All Products" fetches the SAME way, just across every process instead of one
  // subcategory's processes, giving a real, unrestricted whole-building aggregate.
  //
  // Shared by both effects below: fetches WBLCA materials + circularity rows for a set of
  // process ids and resolves the same calculate_combined_data() shape.
  const fetchWblcaCombinedData = (processIds) => {
    if (processIds.length === 0) {
      return Promise.resolve(calculate_combined_data([]));
    }
    return EnvFactor.get_by_process_ids(processIds)
      .then((res) => res.json())
      .then((materialsData) => {
        if (!materialsData || materialsData.length === 0) {
          return calculate_combined_data([]);
        }
        return Circularity.get_by_env_factor_ids(materialsData.map((m) => m.id))
          .then((res) => res.json())
          .then((circResponse) => {
            const circData = circResponse.data || [];
            const t0 = materialsData.map((m) => {
              const c = circData.find((circ) => String(circ.env_id) === String(m.id));
              // calculate_mass (CircularityFunctions.js) silently substitutes a
              // quantity-derived estimate when mass is "NA"/non-numeric — that fallback
              // isn't wanted here, so the raw "did the WBLCA source actually have a mass"
              // state is preserved separately for display to fall back to.
              const rawMass = (c && c.mass !== undefined && c.mass !== null) ? c.mass : m.mass;
              const massIsNA = rawMass === undefined || rawMass === null || rawMass === "" || isNaN(parseFloat(rawMass));
              return {
                ...m,
                ...(c || {}),
                description: m.description || c?.description || "Unknown Material",
                _massIsNA: massIsNA,
              };
            });
            return calculate_combined_data(t0);
          });
      })
      .catch((err) => {
        console.error("Error fetching WBLCA environmental factor data:", err);
        return calculate_combined_data([]);
      });
  };

  useEffect(() => {
    if (processes.length === 0) {
      setProductCombinedData(null);
      return;
    }
    const matchingProcessIds = selectedSubcategoryId === ALL_PRODUCTS_VALUE
      ? processes.map((p) => p.id)
      : processes.filter((p) => p.building_section_subcategory === selectedSubcategoryId).map((p) => p.id);

    fetchWblcaCombinedData(matchingProcessIds).then((result) => {
      setProductCombinedData(result);
      setLoadedSubcategoryId(selectedSubcategoryId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategoryId, processes]);

  // SCI/BCI Calculation are always whole-building — independent of the Products dropdown
  // above, so this refetches only when the building's process list changes.
  useEffect(() => {
    if (processes.length === 0) {
      setAllProductsCombinedData(null);
      return;
    }
    fetchWblcaCombinedData(processes.map((p) => p.id)).then(setAllProductsCombinedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processes]);

  // combined_data (the restricted, always-available set) is only a brief loading
  // placeholder until productCombinedData resolves — passed to Material Composition,
  // Utility, MCI Calculation, Analytics and Design Assessment. Benchmark and the
  // Circularity Report keep using the whole-building combined_data above unconditionally,
  // since they aren't wired to the shared product dropdown.
  const effectiveCombinedData = useMemo(() => {
    if (!productCombinedData) return combined_data;
    return { ...productCombinedData, service_life };
  }, [productCombinedData, combined_data, service_life]);

  // Same loading-placeholder pattern as effectiveCombinedData above, but for the
  // always-whole-building dataset SCI/BCI Calculation use.
  const effectiveAllProductsCombinedData = useMemo(() => {
    if (!allProductsCombinedData) return combined_data;
    return { ...allProductsCombinedData, service_life };
  }, [allProductsCombinedData, combined_data, service_life]);

  // MCI Calculation and Design Assessment must stay empty when "All Products" is selected
  // (from Material Composition/Utility/Analytics) — they only ever show a single
  // subcategory's materials, never the whole-building aggregate. They must also stay empty
  // while the newly picked subcategory's fetch is still in flight (loadedSubcategoryId !==
  // selectedSubcategoryId) — otherwise effectiveCombinedData still holds whatever was
  // fetched for the PREVIOUS selection (e.g. "All Products") and would flash that stale
  // data for a moment before the new fetch resolves.
  const singleProductOnlyCombinedData = useMemo(() => {
    if (selectedSubcategoryId === ALL_PRODUCTS_VALUE || loadedSubcategoryId !== selectedSubcategoryId) {
      return { ...calculate_combined_data([]), service_life };
    }
    return effectiveCombinedData;
  }, [selectedSubcategoryId, loadedSubcategoryId, effectiveCombinedData, service_life]);

  // Same BCI Theoretical/Practical shown in BCI Calculation's footer — shared via
  // computeBciMetrics (shearingLayers.js) so the metric cards always match that table.
  const bciMetrics = useMemo(
    () => computeBciMetrics(effectiveAllProductsCombinedData, processes),
    [effectiveAllProductsCombinedData, processes]
  );

  // effectiveAllProductsCombinedData.mass_sum comes from calculate_combined_data's sumMass,
  // which (like calculate_mass) falls back to a quantity-derived estimate for materials
  // with no real mass — recomputed here excluding _massIsNA materials, same as Material
  // Composition's own Total Mass footer, so the metric card doesn't count that estimate.
  const allProductsMassSum = useMemo(() => {
    return (effectiveAllProductsCombinedData.combined_circularities || []).reduce(
      (s, m) => s + (m._massIsNA ? 0 : (m.mass || 0)),
      0
    );
  }, [effectiveAllProductsCombinedData]);

  // Edits (from any tab, whether "All Products" or a single product is selected) must be
  // merged back into the full circularities array by id (upserting rows that weren't
  // already loaded there, since the restricted top-level fetch may not include them) —
  // never replace the full array with just the edited subset.
  const handleSetCircularities = (updated) => {
    setProductCombinedData((prev) => (prev ? { ...prev, combined_circularities: updated } : prev));
    setCircularities((prevFull) => {
      const byId = new Map(updated.map((item) => [String(item.id), item]));
      const merged = prevFull.map((item) => (byId.has(String(item.id)) ? byId.get(String(item.id)) : item));
      const existingIds = new Set(prevFull.map((item) => String(item.id)));
      const newItems = updated.filter((item) => !existingIds.has(String(item.id)));
      return [...merged, ...newItems];
    });
  };

  const selected_material = useMemo(() => {
    return effectiveCombinedData.combined_circularities.find((m) => m.env_id === selectedEnvFactorID);
  }, [effectiveCombinedData.combined_circularities, selectedEnvFactorID]);

  const handleSmartFill = useCallback(async (currentCircularities, currentMaterials) => {
    if (!currentCircularities?.length || !currentMaterials?.length || aiLoading) return;

    setAiLoading(true);
    aiTriggeredForId.current = selectedBuilding?.id;

    try {
      const sortedMaterials = [...currentMaterials].sort((a, b) => String(a.id).localeCompare(String(b.id)));

      // Extract user context for AI token tracking
      let userContext = {};
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const u = JSON.parse(storedUser);
          userContext = { user_id: u.id || u.user_id, company_id: u.company_id };
        }
      } catch (e) {
        // ignore parse error
      }

      const response = await Circularity.ai_suggest({
        materials: sortedMaterials,
        product_context: {
          name: selectedBuilding?.name || selectedBuilding?.building_name,
          category: selectedBuilding?.building_type || selectedBuilding?.category || selectedBuilding?.type,
          service_life: service_life,
        },
        ...userContext,
      });

      if (!response.ok) throw new Error("AI Suggestion request failed");
      const allSuggestions = await response.json();

      const updatedCircularities = currentCircularities.map((c) => {
        const materialIndex = sortedMaterials.findIndex(m => String(m.id) === String(c.env_id) || String(m.id) === String(c.id));
        const sug = materialIndex !== -1 ? allSuggestions[String(materialIndex)] : null;

        if (sug) {
          // Resolve im_recycled_content: prefer AI suggestion, fall back to existing value
          const aiRecycledContent = sug.im_recycled_content !== null && sug.im_recycled_content !== undefined
            ? Number(sug.im_recycled_content)
            : (c.im_recycled_content !== undefined ? Number(c.im_recycled_content) : 0);

          // im_virgin_content is always derived: 100 - recycled (system rule — never from AI directly)
          const derivedVirginContent = Math.max(0, Math.min(100, 100 - aiRecycledContent));

          // om_recycling: prefer AI suggestion, fall back to existing value
          const aiOmRecycling = sug.om_recycling !== null && sug.om_recycling !== undefined
            ? Number(sug.om_recycling)
            : (c.om_recycling !== undefined ? Number(c.om_recycling) : 80);

          // Industry average service life from AI
          const aiIndustryLife = sug.uf_industry_avg_life !== undefined
            ? Number(sug.uf_industry_avg_life)
            : (sug.rsl !== undefined ? Number(sug.rsl) : (Number(c.uf_industry_avg_life) || 20));

          // Product life: prefer building service_life if set > 0, otherwise AI industry avg, otherwise existing > 0, fallback 20
          const resolvedProductLife = Number(service_life) > 0
            ? Number(service_life)
            : (aiIndustryLife || (Number(c.uf_product_life) > 0 ? Number(c.uf_product_life) : 20));

          const updated = {
            ...c,
            // 1. Utility tab: Reference Service Life & Industry Average
            uf_product_life: resolvedProductLife,
            uf_industry_avg_life: aiIndustryLife,
            // 2. End of Life tab: Recycling Efficiency After Use & Feedstock Efficiency
            eol_recycling_efficiency: sug.eol_recycling_efficiency !== undefined ? Number(sug.eol_recycling_efficiency) : (sug.efficiency !== undefined ? Number(sug.efficiency) : (c.eol_recycling_efficiency || 90)),
            eol_feedstock_efficiency: sug.eol_feedstock_efficiency !== undefined ? Number(sug.eol_feedstock_efficiency) : (c.eol_feedstock_efficiency || 90),
            // 3. Output tab: Sent to Recycling (%)
            om_recycling: aiOmRecycling,
            // 4. Input tab: Recycled Content (%) and derived Virgin Content (%)
            im_recycled_content: aiRecycledContent,
            im_virgin_content: derivedVirginContent,
          };
          return recalculate_row_mci(updated);
        }
        return c;
      });

      setCircularities(updatedCircularities);
      setShowInfoBar(true);
      setAiLoading(false);
      setIsSaveVisible(true);

    } catch (error) {
      console.error("[AI] Error:", error);
      setAiLoading(false);
    }
  }, [selectedBuilding, service_life, aiLoading, setIsSaveVisible]);

  useEffect(() => {
    if (!selectedBuilding?.id) {
      return;
    }
    Building.get_by_id(selectedBuilding.id)
      .then((res) => res.json())
      .then((data) => {
        // Unlike Product/Project's get_by_id (which returns a JSON array),
        // Building's /building/get_by_id returns the row object directly.
        setService_life(data?.service_life || 0);
        setIsSaveVisible(false);
        setIsLoading(false);
        setIsSaveClicked(false);
      });
  }, [selectedBuilding?.id, setIsLoading, setIsSaveClicked, setIsSaveVisible]);

  useEffect(() => {
    if (selectedBuildings.length === 1) {
      setSelectedBuilding(selectedBuildings[0]);
    } else {
      setSelectedBuilding(undefined);
      navigate(routing.sector.buildings.dashboard);
    }
  }, [selectedBuildings]);

  useEffect(() => {
    if (!selectedBuilding?.id) {
      setMaterials([]);
      return;
    }
    EnvFactor.get_materials_by_project_id(selectedBuilding.id)
      .then((response) => response.json())
      .then((data) => {
        setMaterials(data);
      });
  }, [selectedBuilding?.id, refresh]);

  useEffect(() => {
    if (materials.length === 0) {
      setCircularities([]);
      return;
    }
    Circularity.get_by_env_factor_ids(materials.map((item) => item.id))
      .then((res) => res.json())
      .then((response) => {
        const data = response.data || [];
        setCircularities(data);
        setIsNewProduct(!!response.is_new);
      });
  }, [materials]);

  useEffect(() => {
    const alreadyTriggered = aiTriggeredForId.current === selectedBuilding?.id;

    if (isNewProduct && !alreadyTriggered && !aiLoading && circularities.length > 0) {
      const timer = setTimeout(() => {
        handleSmartFill(circularities, materials);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [circularities, materials, isNewProduct, selectedBuilding?.id, aiLoading, handleSmartFill]);

  useEffect(() => {
    if (materials.length === 0) return;

    // IMPORTANT: env_factor IDs are UUIDs (strings). Number() on a UUID = NaN.
    // NaN === NaN is always false. Must use String() comparison — same as ProductCircularity.
    const t0 = materials.map((m) => {
      const c = circularities.find((circ) => String(circ.env_id) === String(m.id));
      return {
        ...m,
        ...(c || {}),
        description: m.description || c?.description || "Unknown Material",
      };
    });

    if (circularities.length > 0) {
      const { mass_sum, gwp_total_sum, cost_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities } = calculate_combined_data(t0);
      setMass_sum(mass_sum);
      setCost_sum(cost_sum);
      setGwp_total_sum(gwp_total_sum);
      setMci_sum(mci_sum);
      setProduct_mci(product_mci);
      setPercentage_product_mci(percentage_product_mci);
      setCombined_circularities(combined_circularities);
    } else {
      // Show materials in the table even if circularity records haven't been created yet
      setCombined_circularities(t0);
    }
  }, [circularities, materials]);

  // ─── Silent MCI Auto-Sync ────────────────────────────────────────────────
  // When the Circularity page computes a non-zero MCI, immediately persist it
  // to building.mci in the DB (debounced 1.5 s) so the Dashboard and Metric
  // cards reflect the live value without the user having to click Save.
  useEffect(() => {
    if (!selectedBuilding?.id || !(percentage_product_mci > 0)) return;

    if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);

    mciSyncTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await Building.get_by_id(selectedBuilding.id);
        const fullBuilding = await res.json();
        if (fullBuilding) {
          await Building.update({
            ...fullBuilding,
            mci: percentage_product_mci,
          });
          console.log('[BuildingCircularity] 🔄 Auto-synced MCI to DB:', (percentage_product_mci * 100).toFixed(2) + '%');
          // Invalidate the dashboard cache so BuildingDashboard picks up the new value
          invalidateDashboard();
        }
      } catch (err) {
        console.error('[BuildingCircularity] ❌ Auto-sync MCI failed:', err);
      }
    }, 1500);

    return () => {
      if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);
    };
  }, [percentage_product_mci, selectedBuilding?.id]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSaveClicked && selectedBuilding) {
      console.log('[BuildingCircularity] 🔄 Fetching latest building data for safe sync...');
      setIsLoading(true);

      const fullPayload = circularities.map(c => ({ ...c, id: Number(c.id) }));

      Promise.all([
        Circularity.update_utilities(fullPayload),
        Circularity.update_end_of_lifes_materials(fullPayload),
        Circularity.update_input_materials(fullPayload),
        Circularity.update_output_materials(fullPayload),
        Building.get_by_id(selectedBuilding.id)
          .then(res => res.json())
          .then(fullBuilding => {
            if (fullBuilding) {
              console.log('[BuildingCircularity] 🔄 Syncing MCI to Dashboard (Full Object):', percentage_product_mci);

              // Safer update: Use full object from DB
              const updatePayload = {
                ...fullBuilding,
                mci: percentage_product_mci,
              };

              return Building.update(updatePayload);
            }
          })
      ])
        .then(() => {
          console.log('[BuildingCircularity] ✅ All data and MCI successfully saved & synced!');
          setIsLoading(false);
          setIsSaveClicked(false);
          setIsSaveVisible(false);
          setRefresh(r => !r);
          setIsNewProduct(false);
          if (enqueueAlert) enqueueAlert({ message: t("Data saved successfully"), severity: "success" });
        })
        .catch(err => {
          console.error('[BuildingCircularity] ❌ Save failed:', err);
          setIsLoading(false);
          setIsSaveClicked(false);
          if (enqueueAlert) enqueueAlert({ message: t("Save failed. Please check your network."), severity: "error" });
        });
    }
  }, [isSaveClicked, selectedBuilding, percentage_product_mci, circularities, setIsLoading, setIsSaveClicked, setIsSaveVisible, setRefresh, enqueueAlert, t]);

  const cardProps = useMemo(
    () => [
      {
        // Whole-building total across all 15 WBLCA subcategories — always "All Products",
        // independent of the Products dropdown (same dataset SCI/BCI Calculation use).
        title: "Total mass",
        subtitle: "Mass of building (all products)",
        value: allProductsMassSum,
        unit: "kg",
        backgroundColorLeft: "rgba(13, 148, 136, 0.1)",
        mainColor: "#0d9488",
        cardIcon: LuWeight,
      },
      {
        // Same value as BCI Calculation's footer (computeBciMetrics, shearingLayers.js) —
        // pre-formatted with truncateTo2Decimals (not rounded) so the two always match
        // exactly; a non-numeric value skips MetricCard's own numeric formatting.
        title: "BCI Theoretical",
        subtitle: "Building Circularity",
        value: truncateTo2Decimals(bciMetrics.bciTheoretical),
        unit: "",
        backgroundColorLeft: "rgba(22, 163, 74, 0.1)",
        mainColor: "#16a34a",
        cardIcon: LuLeaf,
      },
      {
        title: "BCI Practical",
        subtitle: "Building Circularity",
        value: truncateTo2Decimals(bciMetrics.bciPractical),
        unit: "",
        backgroundColorLeft: "rgba(37, 99, 235, 0.1)",
        mainColor: "#2563eb",
        cardIcon: TbBrandCarbon,
      },
      {
        // Same cost_sum the "Cost Total (CAD)" column's Total row shows in MCI
        // Calculation, scoped to all 15 products.
        title: "Total Cost",
        subtitle: "Estimated material cost (all products)",
        value: effectiveAllProductsCombinedData.cost_sum,
        unit: "CAD",
        backgroundColorLeft: "rgba(234, 88, 12, 0.1)",
        mainColor: "#ea580c",
        cardIcon: LuCoins,
      },
    ],
    [effectiveAllProductsCombinedData, bciMetrics, allProductsMassSum]
  );

  const tok = getOrgTokens(isDark);

  return (
    <MainContainer>
      {/* METRIC CARDS SECTION */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(auto-fit, minmax(220px, 1fr))", lg: "repeat(4, 1fr)" }, gap: 2.5 }}>
          {cardProps.map((c, i) => (
            <Box key={i}>
              <MetricCard
                title={t(c.title)}
                subtitle={t(c.subtitle)}
                value={c.value}
                unit={c.unit}
                icon={c.cardIcon}
                accent={c.mainColor}
                bg={c.backgroundColorLeft}
                delay={i * 0.1}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{
        mb: 3,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        width: "100%"
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SegmentedTabs
            options={[
              overview,
              ...(showUtilityTab ? [utility] : []),
              ...(showMciCalcTab ? [mci_calculation] : []),
              design_assessment,
              sci_calculation,
              bci_calculation,
              analytics,
              ...(scopeScenario ? [scenario_what_if] : []),
              ...(showAiRecommendTab ? [benchmarking] : []),
            ]}
            selected={activeTab}
            setSelected={setActiveTab}
            wrap
            dense
          />
        </Box>
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1.5 }}>
          {isAdmin && (
            <CircularityAdminTabControls
              adminTabsPrefs={adminTabsPrefs}
              updatePref={updatePref}
              setAllPrefs={setAllPrefs}
              activeAdminTabsCount={activeAdminTabsCount}
              isDark={isDark}
            />
          )}
          <ReportButton
            active={activeTab === pcds_report.value}
            onClick={() => setActiveTab(pcds_report.value)}
            label={pcds_report.label}
          />
        </Box>
      </Box>

      {activeTab === design_assessment.value && (
        <Box sx={{ mb: 3 }}>
          <DesignAssessmentPanel
            combined_data={singleProductOnlyCombinedData}
            type="Building"
            id={selectedBuilding?.id}
            setCircularities={handleSetCircularities}
            circularities={singleProductOnlyCombinedData.combined_circularities}
            selectedSubcategoryId={selectedSubcategoryId}
            onSubcategoryChange={setSelectedSubcategoryId}
          />
        </Box>
      )}

      {activeTab === scenario_what_if.value && (
        <Box sx={{ mb: 3 }}>
          <ScenarioPanel />
        </Box>
      )}

      {showAiRecommendTab && activeTab === benchmarking.value && (
        <Box sx={{ mb: 3 }}>
          <BenchmarkPanel combined_data={combined_data} />
        </Box>
      )}

      {activeTab === pcds_report.value && (
        <Box sx={{ mb: 3 }}>
          <CircularityReportPanel
            scopeEnergy={scopeEnergy}
            scopeWater={scopeWater}
            scopeEconomic={scopeEconomic}
            reportTitle="Building Circularity"
            productName={selectedBuilding?.name}
            productId={selectedBuilding?.id}
            productSubCategory={selectedBuilding?.sub_category || "Building"}
            serviceLife={service_life}
            combined_data={combined_data}
            boundaryState={boundaryState}
            topic="Building"
          />
        </Box>
      )}

      <Box sx={{ display: activeTab === overview.value ? "block" : "none" }}>
        <BuildingMaterialCompositionTable
          combined_data={effectiveCombinedData}
          activeTab={activeTab}
          tabValue={overview.value}
          selectedSubcategoryId={selectedSubcategoryId}
          onSubcategoryChange={setSelectedSubcategoryId}
        />
      </Box>

      <Box sx={{ display: activeTab === utility.value ? "block" : "none" }}>
        <UtilityTable
          combined_data={effectiveCombinedData}
          activeTab={activeTab}
          tabValue={utility.value}
          setCircularities={handleSetCircularities}
          materials={materials}
          aiLoading={aiLoading}
          handleSmartFill={handleSmartFill}
          scopeEnergy={scopeEnergy}
          scopeWater={scopeWater}
          scopeEconomic={scopeEconomic}
          selectedSubcategoryId={selectedSubcategoryId}
          onSubcategoryChange={setSelectedSubcategoryId}
          topic="Building"
        />
      </Box>

      <Box sx={{ display: activeTab === mci_calculation.value ? "block" : "none" }}>
        <ProjectMCICalculationTable
          combined_data={singleProductOnlyCombinedData}
          topic="Building"
          selectedSubcategoryId={selectedSubcategoryId}
          onSubcategoryChange={setSelectedSubcategoryId}
        />
      </Box>

      <Box sx={{ display: activeTab === sci_calculation.value ? "block" : "none" }}>
        <BuildingSCICalculationTable
          combined_data={effectiveAllProductsCombinedData}
          processes={processes}
          activeTab={activeTab}
          tabValue={sci_calculation.value}
        />
      </Box>

      <Box sx={{ display: activeTab === bci_calculation.value ? "block" : "none" }}>
        <BuildingBCICalculationTable
          combined_data={effectiveAllProductsCombinedData}
          processes={processes}
          activeTab={activeTab}
          tabValue={bci_calculation.value}
        />
      </Box>

      {activeTab === analytics.value && (
        <Box sx={{ display: "flex", flexWrap: "wrap", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <BuildingProductDropdown value={selectedSubcategoryId} onChange={setSelectedSubcategoryId} />
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 12, xl: 6 }} sx={{ height: 520 }}>
              <CircularChart setSelectedEnvFactorID={setSelectedEnvFactorID} title="Material Circularity Indicator" topic="Building" combined_data={effectiveCombinedData} />
            </Grid>
            <Grid size={{ xs: 12, lg: 12, xl: 6 }} sx={{ height: 520 }}>
              <NomarlizedGwpVsPercentageMciChart topic="Building" combined_data={effectiveCombinedData} />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ minHeight: 320, height: "auto" }}>
              <InputMaterialChart height={650} circularities={effectiveCombinedData.combined_circularities} />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ minHeight: 320, height: "auto" }}>
              <OutputMaterialChart height={650} circularities={effectiveCombinedData.combined_circularities} />
            </Grid>
          </Grid>
          {selectedEnvFactorID && (
            <Box sx={{ mt: 2 }}>
              <MaterialDetailCard material={selected_material} />
            </Box>
          )}
        </Box>
      )}

      {/* INFO BANNER */}
      <InfoBanner
        show={showInfoBar}
        title="Intelligent Estimation Applied"
        message={t("Recycling efficiency and product life parameters have been estimated for the identified materials ({{count}} items). Please review and Save.", { count: materials.length })}
        onClose={() => setShowInfoBar(false)}
        isDark={isDark}
      />

      <Box sx={{ mt: 4 }}>
        <SaveButton />
      </Box>
    </MainContainer>
  );
};

export default BuildingCircularity;
